/**
 * Validates that the given text is a valid SVG element with no surrounding text.
 * Requires the SVG to have xmlns, viewBox, or width/height attributes to filter out
 * well-formed but meaningless XML that happens to use an svg tag.
 * Returns the cleaned SVG string if valid, or null if invalid.
 */
export function validateSvgText(text: string): string | null {
  const trimmed = text.trim();

  if (!trimmed.startsWith("<")) return null;
  if (!trimmed.endsWith(">")) return null;

  // Handle optional XML declaration: <?xml ... ?>
  let svgContent = trimmed;
  if (trimmed.startsWith("<?xml")) {
    const xmlDeclEnd = trimmed.indexOf("?>");
    if (xmlDeclEnd === -1) return null;
    svgContent = trimmed.slice(xmlDeclEnd + 2).trim();
  }

  if (!svgContent.startsWith("<svg")) return null;

  // Parse as XML to validate structure
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, "image/svg+xml");

    const parseError = doc.querySelector("parsererror");
    if (parseError) return null;

    const svg = doc.documentElement;
    if (svg.tagName.toLowerCase() !== "svg") return null;

    // Require at least one of: xmlns, viewBox, or width/height
    // This filters out well-formed XML that isn't actually a useful SVG
    const hasXmlns = svg.hasAttribute("xmlns"),
      hasViewBox = svg.hasAttribute("viewBox"),
      hasDimensions = svg.hasAttribute("width") || svg.hasAttribute("height");

    if (!hasXmlns && !hasViewBox && !hasDimensions) return null;

    return trimmed;
  } catch {
    return null;
  }
}
